import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const splitName = (full) => {
  const parts = (full || "").split(" ").filter(Boolean).map((w) => w.charAt(0) + w.slice(1).toLowerCase());
  return { cognome: parts[0] || "", nome: parts.slice(1).join(" ") };
};

export const AddFromBatchDialog = ({ open, onOpenChange, batch, onDone }) => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(batch.items.filter((i) => i.match_status === "non_trovato")
      .map((i) => ({ index: i.index, cf: i.codice_fiscale, email: "", ...splitName(i.nome_rilevato) })));
  }, [open, batch]);

  const set = (idx, key, val) => setRows((r) => r.map((row) => (row.index === idx ? { ...row, [key]: val } : row)));

  const save = async () => {
    const valid = rows.filter((r) => r.nome.trim() && r.cognome.trim());
    if (!valid.length) return toast.error("Compila almeno nome e cognome");
    setSaving(true);
    try {
      const { data } = await api.post(`/batches/${batch.id}/add-employees`, { employees: valid });
      toast.success(`${data.created} dipendenti aggiunti in anagrafica`);
      data.errors.forEach((e) => toast.error(e));
      onDone(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="add-from-batch-dialog">
        <DialogHeader>
          <DialogTitle>Nuovi dipendenti trovati nel PDF</DialogTitle>
          <DialogDescription>Controlla nome e cognome e aggiungi l'email: verranno salvati in anagrafica con il loro codice fiscale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.index} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-3" data-testid={`new-employee-row-${r.index}`}>
              <p className="font-mono text-xs text-slate-500 sm:col-span-3">{r.cf}</p>
              <Input placeholder="Cognome" value={r.cognome} onChange={(e) => set(r.index, "cognome", e.target.value)} data-testid={`new-employee-cognome-${r.index}`} />
              <Input placeholder="Nome" value={r.nome} onChange={(e) => set(r.index, "nome", e.target.value)} data-testid={`new-employee-nome-${r.index}`} />
              <Input placeholder="Email" type="email" value={r.email} onChange={(e) => set(r.index, "email", e.target.value)} data-testid={`new-employee-email-${r.index}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="add-from-batch-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="add-from-batch-submit">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Aggiungi in anagrafica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
